import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Trophy, Calendar, ChevronLeft, ChevronRight, AlertCircle, 
  Tv, MapPin, ExternalLink, Filter, RefreshCw, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    timeZone: 'America/New_York'
  });
};

// Format time for display
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    timeZone: 'America/New_York'
  });
};

// Game Card Component
const GameCard = ({ game }) => {
  const [away, home] = game.teams || [];
  const isLive = game.state === 'in';
  const isFinal = game.state === 'post';
  
  return (
    <div 
      className={`bg-white rounded-xl overflow-hidden shadow-lg border-2 ${
        isLive ? 'border-green-500 ring-2 ring-green-500/30' : 
        isFinal ? 'border-gray-300' : 'border-gray-200'
      } hover:shadow-xl transition-all`}
      data-testid={`game-${game.id}`}
    >
      {/* Status Bar */}
      <div className={`px-4 py-2 flex items-center justify-between ${
        isLive ? 'bg-green-600 text-white' : 
        isFinal ? 'bg-gray-700 text-white' : 'bg-blue-900 text-white'
      }`}>
        <div className="flex items-center gap-2">
          {isLive && <span className="w-2 h-2 bg-white rounded-full animate-pulse" />}
          <span className="font-bold text-sm uppercase">
            {isLive ? 'LIVE' : isFinal ? 'FINAL' : formatDate(game.date)}
          </span>
        </div>
        <span className="text-xs opacity-90">{game.detail}</span>
      </div>
      
      {/* Teams */}
      <div className="p-4">
        {/* Away Team */}
        <div className={`flex items-center justify-between py-2 ${away?.winner ? 'font-bold' : ''}`}>
          <div className="flex items-center gap-3">
            <img 
              src={away?.logo || 'https://via.placeholder.com/40?text=?'} 
              alt={away?.name}
              className="w-10 h-10 object-contain"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
            />
            <div>
              <div className={`${away?.winner ? 'text-red-700' : 'text-gray-900'}`}>
                {away?.name || 'TBD'}
              </div>
              {away?.record && (
                <div className="text-xs text-gray-500">({away.record})</div>
              )}
            </div>
          </div>
          <span className={`text-2xl font-bold ${
            away?.winner ? 'text-red-700' : 'text-gray-700'
          }`}>
            {(isLive || isFinal) ? (away?.score ?? '-') : ''}
          </span>
        </div>
        
        {/* Divider */}
        <div className="border-t border-gray-200 my-1" />
        
        {/* Home Team */}
        <div className={`flex items-center justify-between py-2 ${home?.winner ? 'font-bold' : ''}`}>
          <div className="flex items-center gap-3">
            <img 
              src={home?.logo || 'https://via.placeholder.com/40?text=?'} 
              alt={home?.name}
              className="w-10 h-10 object-contain"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
            />
            <div>
              <div className={`${home?.winner ? 'text-red-700' : 'text-gray-900'}`}>
                {home?.name || 'TBD'}
              </div>
              {home?.record && (
                <div className="text-xs text-gray-500">({home.record})</div>
              )}
            </div>
          </div>
          <span className={`text-2xl font-bold ${
            home?.winner ? 'text-red-700' : 'text-gray-700'
          }`}>
            {(isLive || isFinal) ? (home?.score ?? '-') : ''}
          </span>
        </div>
      </div>
      
      {/* Game Info Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {!isLive && !isFinal && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatTime(game.date)} ET</span>
            </div>
          )}
          {game.venue && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{game.venue}{game.city ? `, ${game.city}` : ''}</span>
            </div>
          )}
          {game.broadcast && (
            <div className="flex items-center gap-1">
              <Tv className="w-3 h-3" />
              <span>{game.broadcast}</span>
            </div>
          )}
        </div>
        {game.url && (
          <a 
            href={game.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2"
          >
            <ExternalLink className="w-3 h-3" />
            ESPN Details
          </a>
        )}
        {game.article && (
          <Link
            to={game.article.url}
            className="inline-flex items-center gap-1 text-[#DC2626] font-semibold hover:underline mt-2 ml-4"
            data-testid={`scoreboard-article-link-${game.id}`}
          >
            Read: {game.article.title || 'Game coverage'} →
          </Link>
        )}
      </div>
    </div>
  );
};

export default function SWACScoreboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [teamFilter, setTeamFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const refreshInterval = useRef(null);
  
  // Calculate week start based on offset (Tuesday-Monday)
  const getWeekStart = useCallback((offset = 0) => {
    const now = new Date();
    // Adjust to Eastern time
    const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const day = eastern.getDay();
    // Tuesday is day 2, calculate days since last Tuesday
    const daysSinceTuesday = (day + 5) % 7;
    const tuesday = new Date(eastern);
    tuesday.setDate(eastern.getDate() - daysSinceTuesday + (offset * 7));
    return tuesday.toISOString().split('T')[0];
  }, []);
  
  const fetchScoreboard = useCallback(async () => {
    try {
      const start = getWeekStart(weekOffset);
      const response = await fetch(`${BACKEND_URL}/api/swac-scoreboard?start=${start}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch scoreboard: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
      setError(result.message || null);
    } catch (err) {
      console.error('Scoreboard fetch error:', err);
      setError('Unable to load scoreboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [weekOffset, getWeekStart]);
  
  // Initial fetch and refresh interval
  useEffect(() => {
    setLoading(true);
    fetchScoreboard();
    
    // Refresh every 60 seconds
    refreshInterval.current = setInterval(fetchScoreboard, 60000);
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [fetchScoreboard]);
  
  // Handle visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        fetchScoreboard();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchScoreboard]);
  
  // Get unique teams for filter
  const allTeams = data?.games?.reduce((teams, game) => {
    game.teams?.forEach(t => {
      if (t.name && !teams.includes(t.name)) {
        teams.push(t.name);
      }
    });
    return teams;
  }, []).sort() || [];
  
  // Filter games
  const filteredGames = data?.games?.filter(game => {
    // Team filter
    if (teamFilter && !game.teams?.some(t => t.name === teamFilter)) {
      return false;
    }
    // Status filter
    if (statusFilter === 'live' && game.state !== 'in') return false;
    if (statusFilter === 'final' && game.state !== 'post') return false;
    if (statusFilter === 'upcoming' && game.state !== 'pre') return false;
    return true;
  }) || [];
  
  // Format week range for display
  const formatWeekRange = () => {
    if (!data?.start || !data?.end) return '';
    const start = new Date(data.start + 'T00:00:00');
    const end = new Date(data.end + 'T00:00:00');
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };
  
  return (
    <>
      <Helmet>
        <title>SWAC Scoreboard | Live Scores | SheLovesThee</title>
        <meta name="description" content="Live SWAC football scores, schedules, and game results. Powered by ESPN." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#001f3f] to-[#003366]">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-700 py-8 shadow-lg">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Trophy className="w-10 h-10 text-yellow-400" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                    SWAC Scoreboard
                  </h1>
                  <p className="text-yellow-300 text-sm">Powered by ESPN • Updates every 60 seconds</p>
                </div>
              </div>
              <Link 
                to="/swac-pickem"
                className="bg-yellow-400 hover:bg-yellow-500 text-red-900 font-bold px-4 py-2 rounded-lg transition-colors hidden md:block"
              >
                Make Picks
              </Link>
            </div>
          </div>
        </div>
        
        {/* Controls */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            {/* Week Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setWeekOffset(w => w - 1)}
                className="flex items-center gap-1 text-white hover:text-yellow-400 transition-colors"
                aria-label="Previous week"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Previous</span>
              </button>
              
              <div className="text-center">
                <div className="flex items-center gap-2 justify-center">
                  <Calendar className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-bold text-lg">{formatWeekRange()}</span>
                </div>
                {weekOffset !== 0 && (
                  <button
                    onClick={() => setWeekOffset(0)}
                    className="text-yellow-400 hover:text-yellow-300 text-sm mt-1"
                  >
                    Return to Current Week
                  </button>
                )}
              </div>
              
              <button
                onClick={() => setWeekOffset(w => w + 1)}
                className="flex items-center gap-1 text-white hover:text-yellow-400 transition-colors"
                aria-label="Next week"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-white/70" />
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="bg-white/10 border border-white/30 text-white rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">All Teams</option>
                  {allTeams.map(team => (
                    <option key={team} value={team} className="text-gray-900">{team}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white/10 border border-white/30 text-white rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="all">All Games</option>
                  <option value="live" className="text-gray-900">Live Now</option>
                  <option value="final" className="text-gray-900">Final</option>
                  <option value="upcoming" className="text-gray-900">Upcoming</option>
                </select>
              </div>
              
              <button
                onClick={fetchScoreboard}
                className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-sm ml-auto"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            
            {/* Last Updated / Stale Warning */}
            {data && (
              <div className="mt-3 text-xs text-white/60 flex items-center gap-2">
                <Clock className="w-3 h-3" />
                Last updated: {new Date(data.updatedAt).toLocaleTimeString()}
                {data.stale && (
                  <span className="text-yellow-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {data.message || 'Showing cached data'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Games Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-12">
          {loading && !data ? (
            <div className="text-center py-16">
              <RefreshCw className="w-12 h-12 text-yellow-400 animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Loading scoreboard...</p>
            </div>
          ) : error && !data?.games?.length ? (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-white text-lg">{error}</p>
              <button
                onClick={fetchScoreboard}
                className="mt-4 bg-yellow-400 hover:bg-yellow-500 text-red-900 font-bold px-6 py-2 rounded-lg"
              >
                Try Again
              </button>
            </div>
          ) : filteredGames.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-white text-lg">No games found for this week</p>
              <p className="text-white/60 text-sm mt-2">Try adjusting your filters or navigating to a different week</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGames.map(game => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          )}
        </div>
        
        {/* Attribution */}
        <div className="text-center pb-8">
          <p className="text-white/40 text-xs">
            Data provided by ESPN. Updates every 60 seconds on Saturdays, every 15 minutes otherwise.
          </p>
        </div>
      </div>
    </>
  );
}
